import React, { useState, useEffect } from 'react';
import { locationAPI } from '../services/api';
import SearchableDropdown from './SearchableDropdown';

// Types
interface Country {
  code: string;
  name: string;
  states: number;  // Changed from 'locations' to 'states'
}

interface State {
  code: string;
  name: string;
  cities: number;
}

interface City {
  code: string;
  name: string;
}

interface Location {
  country?: string;
  state?: string;
  city?: string;
}

interface LocationSelectorProps {
  value?: Location;
  onChange: (location: Location, locationString: string) => void;
  disabled?: boolean;
  className?: string;
}

const LocationSelector: React.FC<LocationSelectorProps> = ({
  value = {},
  onChange,
  disabled = false,
  className = ''
}) => {
  // State
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  
  // Loading states
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Load countries on mount
  useEffect(() => {
    loadCountries();
  }, []);

  // Load states when country changes
  useEffect(() => {
    if (value.country) {
      loadStates(value.country);
    } else {
      setStates([]);
      setCities([]);
    }
  }, [value.country]);

  // Load cities when state changes
  useEffect(() => {
    if (value.country && value.state) {
      loadCities(value.country, value.state);
    } else {
      setCities([]);
    }
  }, [value.country, value.state]);

  // API calls
  const loadCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await locationAPI.getCountries();
      setCountries(response.countries || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  };

  const loadStates = async (countryCode: string) => {
    try {
      setLoadingStates(true);
      const response = await locationAPI.getStates(countryCode);
      setStates(response.states || []);
    } catch (error) {
      console.error('Error loading states:', error);
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const loadCities = async (countryCode: string, stateCode: string) => {
    try {
      setLoadingCities(true);
      const response = await locationAPI.getCities(countryCode, stateCode);
      setCities(response.cities || []);
    } catch (error) {
      console.error('Error loading cities:', error);
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  // Generate location string from selection
  const generateLocationString = (location: Location): string => {
    const parts = [location.country];
    if (location.state) parts.push(location.state);
    if (location.city) parts.push(location.city);
    return parts.filter(Boolean).join('-');
  };

  // Handle selection changes
  const handleCountryChange = (countryCode: string) => {
    const newLocation = { country: countryCode };
    const locationString = generateLocationString(newLocation);
    onChange(newLocation, locationString);
  };

  const handleStateChange = (stateCode: string) => {
    const newLocation = { ...value, state: stateCode, city: undefined };
    const locationString = generateLocationString(newLocation);
    onChange(newLocation, locationString);
  };

  const handleCityChange = (cityCode: string) => {
    const newLocation = { ...value, city: cityCode };
    const locationString = generateLocationString(newLocation);
    onChange(newLocation, locationString);
  };

  const handleClearState = () => {
    const newLocation = { country: value.country };
    const locationString = generateLocationString(newLocation);
    onChange(newLocation, locationString);
  };

  const handleClearCity = () => {
    const newLocation = { country: value.country, state: value.state };
    const locationString = generateLocationString(newLocation);
    onChange(newLocation, locationString);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Country Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          Country *
        </label>
        <SearchableDropdown
          options={countries.map(country => ({
            value: country.code,
            label: `${country.name} (${country.states.toLocaleString()} states)`
          }))}
          value={value.country || ''}
          onChange={handleCountryChange}
          placeholder={loadingCountries ? 'Loading countries...' : 'Select a country'}
          disabled={disabled}
          loading={loadingCountries}
        />
      </div>

      {/* State Selector - Always show when country is selected */}
      {value.country && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-300">
              State/Region
            </label>
            {value.state && (
              <button
                type="button"
                onClick={handleClearState}
                className="text-xs text-gray-400 hover:text-gray-200"
              >
                Any state
              </button>
            )}
          </div>
          <SearchableDropdown
            options={states.map(state => ({
              value: state.code,
              label: `${state.name} (${state.cities} cities)`
            }))}
            value={value.state || ''}
            onChange={handleStateChange}
            placeholder={loadingStates ? 'Loading states...' : 'Any state/region'}
            disabled={disabled}
            loading={loadingStates}
          />
        </div>
      )}

      {/* City Selector - Always show when country is selected */}
      {value.country && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-300">
              City
            </label>
            {value.city && (
              <button
                type="button"
                onClick={handleClearCity}
                className="text-xs text-gray-400 hover:text-gray-200"
              >
                Any city
              </button>
            )}
          </div>
          <SearchableDropdown
            options={cities.map(city => ({
              value: city.code,
              label: city.name
            }))}
            value={value.city || ''}
            onChange={handleCityChange}
            placeholder={!value.state ? 'Select a state first' : loadingCities ? 'Loading cities...' : 'Any city'}
            disabled={disabled || !value.state}
            loading={loadingCities}
          />
        </div>
      )}


    </div>
  );
};

export default LocationSelector; 